import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

const ROLE_OPTIONS = [
  "STUDENT", "PARENT", "OWNER", "MANAGER", "INSTITUTION",
  "ADMIN", "MESS", "TRANSPORTER", "DIRECTOR", "TRUSTEE",
  "HUMA", "COORDINATOR",
];

const ONBOARDING_TYPES = ["SELF", "INSTITUTIONAL", "BUSINESS", "ADMIN"];

const SOURCE_CHANNELS = [
  "SELF", "DIRECTOR", "HUMA", "INSTITUTION",
  "SOCIAL_MEDIA", "WEBSITE", "CAMPAIGN", "OTHER",
];

const HAFHOnboarding = ({ t, selectedLanguage, role, isNew }) => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const userId = authState?.user?._id || null;
  const userCode = authState?.user?.think5whyscode || "";
  const userName = authState?.user?.name || "";
  const token = authState?.token;
  const email = authState?.user?.email || "";
  const mobile = authState?.user?.mobile || "";

  const emptyForm = {
    userCode,
    userName,
    onboardingType: "SELF",
    sourceChannel: "SELF",
    referredByCode: "",
    referredByName: "",
    onboardedByCode: "SELF",
    onboardedByName: "Self Onboarding",
    preferredActiveRole: "STUDENT",
    mobileNumber: mobile,
    emailId: email,
    remarks: "",
  };

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  // const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [showExtraInputs, setShowExtraInputs] = useState(false);

  // const isReadOnly = !isNew; // ✅ KEY FLAG
  // const isEditMode = Boolean(editId);
  const isReadOnly = false;  // Deliberatly taken for later use

  const buildFilledForm = (record, userCode, userName) => ({
    userId: record.userId || "",
    userCode: record.userCode || userCode || "",
    userName: record.userName || userName || "",
    mobileNumber: record.mobileNumber || "",
    emailId: record.emailId || "",
    sourceChannel: record.sourceChannel || "SELF",
    roles: record.roles || [],
    preferredActiveRole: record.preferredActiveRole || "NONE",
    isStudent: record.isStudent ?? false,
    isParent: record.isParent ?? false,
    isOwner: record.isOwner ?? false,
    isManager: record.isManager ?? false,
    isInstitution: record.isInstitution ?? false,
    isMess: record.isMess ?? false,
    isTrustee: record.isTrustee ?? false,
    isDirector: record.isDirector ?? false,
    isHUMA: record.isHUMA ?? false,
    isAdmin: record.isAdmin ?? false,
    isTransporter: record.isTransporter ?? false,
    isCoordinator: record.isCoordinator ?? false,
    onboardingType: record.onboardingType || "SELF",
    onboardedByCode: record.onboardedByCode || "SELF",
    onboardedByName: record.onboardedByName || "Self Onboarding",

    referredBy: record.referredBy || null,
    referredByCode: record.referredByCode || "",
    referredByName: record.referredByName || "",
    isDemoAccount: record.isDemoAccount ?? true,
    demoDays: record.demoDays ?? 7,
    isVerified: record.isVerified ?? false,
    isActive: record.isActive ?? true,
    remarks: record.remarks || "",

  });

  const fetchMyOnboarding = async () => {
    try {
      setLoading(true);

      const res = await fetchData({
        url: "/hafh/business/onboarding/my",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      });

      if (res?.status && res.record) {
        setForm({
          ...res.record,
          preferredActiveRole: res.record?.preferredActiveRole || "STUDENT",
        });
      } else {
        setForm(emptyForm);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchMyOnboarding();
  }, [userId, isNew]);

  const selectRole = (role) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      preferredActiveRole: role,
    }));
  };

  const handleCreate = async () => {
    if (isReadOnly) return;
  // VALIDATIONS
    if (!form.referredByCode?.trim() || form.referredByCode.length !== 14)
      return setMsg("❌ Valid 14 character Referred By Code required");

    if (!form.referredByName?.trim())
      return setMsg("❌ Referred By Name required");

    if (!form.mobileNumber?.trim() || form.mobileNumber.length !== 10)
      return setMsg("❌ Valid 10 digit mobile number required");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId || ""))
      return setMsg("❌ Valid email required");

    try {
      setLoading(true);
      setMsg("");

      const payload = {
        ...form,
        preferredActiveRole: form.preferredActiveRole || "STUDENT",
        roles: form.preferredActiveRole ? [form.preferredActiveRole] : [],
      };

      const res = await fetchData({
        url: "/hafh/business/onboarding/create",
        method: "post",
        data: payload,
        headers: { Authorization: token },
      });

      if (res?.status) {
        setIsOnboarded(true);
        setMsg(
          `✅ Successfully Onboarded as ${form.preferredActiveRole}. Refresh Screen to update your complete profile.`
        );
      }
    } catch (err) {
      setMsg("❌ Server error while creating");
    } finally {
      setLoading(false);
    }
  };

  const [acquiringRole, setAcquiringRole] = useState(false);
  const [acquiredRoles, setAcquiredRoles] = useState([]);

  const handleAcquireRole = async () => {
    try {
      setAcquiringRole(true);
      setMsg("");

      const res = await fetchData({
        url: "/hafh/business/onboarding/acquire-role",
        method: "put",
        data: {
          role,
        },
        headers: {
          Authorization: token,
        },
      });

      if (res?.status) {
        setMsg(`✅ ${role} role acquired successfully`);

        setAcquiredRoles((prev) => [...prev, role]);

        setForm((prev) => ({
          ...prev,
          roles: [...(prev?.roles || []), role],
        }));
      } else {
        setMsg(res?.msg || "❌ Failed to acquire role");
      }

    } catch (err) {
      setMsg("❌ Server error while acquiring role");

    } finally {
      setAcquiringRole(false);
    }
  };

  // TODO For Deactivating Helper in Admin
  const handleDeactivate = async () => {

    await updateFlag({
      token,
      tableName: "HAFHOnboarding",
      searchKey: "_id",
      searchValue: userId, // or onboardingId
      flagKey: "isActive",
      flagValue: false
    });

  };

  if (loading) {
    return (
      <div className="bg-gray-900 p-5 rounded-xl text-white">
        Loading onboarding...
      </div>
    );
  }

  const Input = ({ label, value, onChange }) => (
    <div>
      <label className="text-xs text-gray-300">{label}</label>
      <input
        className="w-full bg-gray-700 p-3 rounded-md"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );

  const SectionTitle = ({ title }) => (
    <div className="col-span-1 lg:col-span-2 mt-2">
      <h4 className="text-sm text-cyan-200 font-semibold">{title}</h4>
    </div>
  );

  const feeFields = [
    { key: "serviceFeePercent", label: "Service Fee %", value: form?.serviceFeePercent ?? 0 },
    { key: "onboardingFeeStudent", label: "Onboarding Fee Student", value: 0 },
    { key: "onboardingFeeParent", label: "Onboarding Fee Parent", value: 0 },
    { key: "onboardingFeeOwner", label: "Onboarding Fee Owner", value: 100 },
    { key: "onboardingFeeTrustee", label: "Onboarding Fee Trustee", value: 200 },
    { key: "onboardingFeeInstitution", label: "Onboarding Fee Institution", value: 5000 },
    { key: "onboardingFeeMess", label: "Onboarding Fee Mess", value: 500 },
    { key: "onboardingFeeTransporter", label: "Onboarding Fee Transporter", value: 500 },
    { key: "onboardingFeeAdmin", label: "Onboarding Fee Admin", value: 0 },
    { key: "onboardingFeeDirector", label: "Onboarding Fee Director", value: 1000 },
    { key: "onboardingFeeCoordinator", label: "Onboarding Fee Coordinator", value: 250 },
    { key: "onboardingFeeHUMA", label: "Onboarding Fee HUMA", value: 250 },
  ];

  return (
    <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white">
{/* <div className="text-xs text-gray-300 bg-gray-900 p-3 rounded">
  <pre>{JSON.stringify(authState, null, 2)}</pre>
</div>    
<div className="text-xs text-gray-300 bg-gray-900 p-3 rounded">
  <pre>{JSON.stringify(emptyForm, null, 2)}</pre>
</div>       */}
      {/* STATUS */}
      {msg && (
        <div className="text-sm text-green-400">{msg}</div>
      )}

      {!isNew && (
        <div className="text-xs text-yellow-400 bg-gray-800 p-2 rounded">
          Onboarding already completed!! Update Other Sections as allowed to you {role}
        </div>
      )}

      {/* ================= FEES ================= */}
      <div className="mt-2 border-t border-yellow-100">
        <h2 className="text-yellow-200 text-sm lg:text-lg font-semibold">
          Fees Configuration
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {feeFields.map((field) => (
            <div
              key={field.key}
              className="bg-gray-800 p-3 rounded-md border border-gray-700"
            >
              <p className="text-xs lg:text-sm text-gray-200">
                {field.label}
              </p>

              <p className="text-white font-semibold text-sm lg:text-base">
                ₹ {Number(field.value).toFixed(2)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {!isNew && (
        <>
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={handleAcquireRole}
              disabled={
                acquiringRole ||
                acquiredRoles.includes(role) ||
                form?.roles?.includes(role)
              }
              className="bg-teal-600 hover:bg-teal-800 px-6 py-3 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {form?.roles?.includes(role)
                ? `${role} Role Already Acquired`
                : acquiringRole
                ? "Acquiring Role..."
                : `Acquire New Role ${role}`}
            </button>

            {msg && (
              <div className="text-xs lg:text-sm text-yellow-300">
                {msg}
              </div>
            )}
          </div>
        </>
      )}

      {isNew && (
        <>
        <div className="bg-gray-800 p-5 rounded-xl space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs lg:text-sm text-gray-300 font-medium">Preferred Active Role</label>
              <select
                value={form.preferredActiveRole || ""}
                onChange={(e) => selectRole(e.target.value)}
                disabled={isReadOnly}
                className="w-full bg-gray-700 border border-gray-600 text-white p-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            
            {/* ONBOARDING TYPE */}
            <div className="space-y-2">
              <label className="text=xs lg:text-sm text-gray-300 font-medium">Onboarding Type</label>

              <select
                value={form.onboardingType}
                onChange={(e) =>
                  !isReadOnly &&
                  setForm({ ...form, onboardingType: e.target.value })
                }
                disabled={isReadOnly}
                className="w-full bg-gray-700 p-1 rounded-md"
              >
                {ONBOARDING_TYPES.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* REFERRAL */}
        <div className="bg-gray-800 p-5 rounded-xl space-y-4">
          <h3 className="text-cyan-500 text-lg font-semibold">
            Referral Information (Compulsory)
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-300">Referred By Code *</label>
              <input
                required
                maxLength={14}
                placeholder="Enter 14 character Think5Whys Code"
                className="w-full bg-gray-700 p-1 rounded-md"
                value={form.referredByCode}
                onChange={(e) => !isReadOnly && setForm({ ...form, referredByCode: e.target.value.toUpperCase() })}
                disabled={isReadOnly}
              />
              {form.referredByCode && form.referredByCode.length !== 14 && (
                <p className="text-red-400 text-xs mt-1">
                  Referred Code must be exactly 14 characters
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-300">Referred By Name *</label>
              <input
                required
                className="w-full bg-gray-700 p-1 rounded-md"
                value={form.referredByName}
                onChange={(e) => !isReadOnly && setForm({ ...form, referredByName: e.target.value })}
                disabled={isReadOnly}
              />
              {!form.referredByName?.trim() && (
                <p className="text-red-400 text-xs mt-1">
                  Referred Name is required
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-300">Mobile Number *</label>
              <input
                required
                type="tel"
                maxLength={10}
                className="w-full bg-gray-700 p-1 rounded-md"
                value={form.mobileNumber}
                onChange={(e) => setForm({ ...form, mobileNumber: e.target.value.replace(/\D/g, "") })}
              />

              {form.mobileNumber && form.mobileNumber.length !== 10 && (
                <p className="text-red-400 text-xs mt-1">
                  Mobile Number must be 10 digits
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-300">Email ID *</label>
              <input
                required
                type="email"
                className="w-full bg-gray-700 p-1 rounded-md"
                value={form.emailId}
                onChange={(e) => setForm({ ...form, emailId: e.target.value })}
              />

              {!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId || "") && (
                <p className="text-red-400 text-xs mt-1">
                  Enter valid email address
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleCreate}
            disabled={isOnboarded}
            className="bg-teal-600 hover:bg-teal-800 px-6 py-3 rounded-md font-medium disabled:opacity-80"
          >
            Create Onboarding
          </button>

          {msg && (
            <div className="text-xs lg:text-sm text-yellow-300">
              {msg}
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default HAFHOnboarding;